import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
    SearchIcon, DownloadIcon, UserIcon, FilterIcon, EyeIcon, XIcon, BriefcaseIcon 
} from '../../components/icons/Icons';

// --- HELPERS ---
const getFinancialYear = (dateObj) => {
    if (!dateObj) return '';
    const date = new Date(dateObj);
    const month = date.getMonth(); 
    if (month < 3) return `${date.getFullYear() - 1}-${date.getFullYear()}`;
    return `${date.getFullYear()}-${date.getFullYear() + 1}`;
};

const UserAvatar = ({ url, name, className = "h-8 w-8" }) => {
    const [imgError, setImgError] = useState(false);
    if (url && !imgError) {
        return <img src={url} alt={name} className={`${className} rounded-full object-cover border border-gray-200`} onError={() => setImgError(true)} />;
    }
    return (
        <div className={`${className} rounded-full bg-blue-100 flex items-center justify-center text-[#213368] font-bold text-xs shrink-0 border border-blue-200`}>
            {name ? name.charAt(0).toUpperCase() : <UserIcon className="h-4 w-4" />}
        </div>
    );
};

export default function HROversightPanel({ appId }) {
    const [allUsers, setAllUsers] = useState([]);
    const [allActivities, setAllActivities] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [selectedYear, setSelectedYear] = useState(getFinancialYear(new Date()));
    const [yearOptions, setYearOptions] = useState([getFinancialYear(new Date())]);
    const [deptFilter, setDeptFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('Approved'); // 'Approved' = Active, 'Deactivated' = Past
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Modal State
    const [selectedMember, setSelectedMember] = useState(null);

    // 1. Fetch Data (Re-fetch when statusFilter changes)
    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                // A. Fetch Users based on Status (Active vs Past)
                const usersRef = collection(db, `artifacts/${appId}/users`);
                const qUsers = query(usersRef, where('status', '==', statusFilter));
                const userSnap = await getDocs(qUsers);
                const userList = userSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                setAllUsers(userList);

                // B. Fetch All Completed Activities
                // We fetch all to support historical year filtering instantly
                const activitiesRef = collection(db, `artifacts/${appId}/pd_activities`);
                const qActivities = query(activitiesRef, orderBy('createdAt', 'desc')); 
                const actSnap = await getDocs(qActivities);
                
                const activityList = actSnap.docs.map(d => {
                    const data = d.data();
                    const date = data.date ? data.date.toDate() : data.createdAt.toDate();
                    return {
                        id: d.id,
                        ...data,
                        fy: getFinancialYear(date)
                    };
                });
                
                setAllActivities(activityList);

                // Set Years from data
                const years = new Set([getFinancialYear(new Date())]);
                activityList.forEach(a => { if(a.fy) years.add(a.fy); });
                setYearOptions(Array.from(years).sort().reverse());

            } catch (error) {
                console.error("Error loading HR report:", error);
            }
            setLoading(false);
        };
        fetchAllData();
    }, [appId, statusFilter]);

    // 2. Process Data (Filter -> Map -> Sort)
    const tableData = useMemo(() => {
        // Filter Users
        let filteredUsers = allUsers.filter(u => {
            const matchesDept = deptFilter === 'All' || u.department === deptFilter;
            const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesDept && matchesSearch;
        });

        // Map Activities & Calculate Stats
        const mappedData = filteredUsers.map(user => {
            const userActs = allActivities.filter(a => 
                a.userId === user.id && 
                a.status === 'Completed' && 
                a.fy === selectedYear
            );

            const stats = {
                Technical: 0, 'Soft Skills': 0, 'Peer Learning': 0, Knowledge: 0, Total: 0
            };

            userActs.forEach(a => {
                const type = a.type === 'Technical' ? 'Technical' :
                             a.type === 'Soft Skills' ? 'Soft Skills' :
                             a.type === 'Peer Learning' ? 'Peer Learning' : 'Knowledge';
                stats[type] += (Number(a.hours) || 0);
                stats.Total += (Number(a.hours) || 0);
            });

            return { ...user, stats, activities: userActs };
        });

        // Sort Alphabetically by Name
        return mappedData.sort((a, b) => a.name.localeCompare(b.name));

    }, [allUsers, allActivities, selectedYear, deptFilter, searchTerm]);

    const uniqueDepts = useMemo(() => ['All', ...new Set(allUsers.map(u => u.department).filter(Boolean))], [allUsers]);

    // Pagination Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = tableData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(tableData.length / itemsPerPage);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, deptFilter, searchTerm, selectedYear]);

    // Export to CSV
    const handleExport = () => {
        const headers = ["Name", "Status", "Department", "Technical (Hrs)", "Soft Skills (Hrs)", "Knowledge (Hrs)", "Peer (Hrs)", "Total (Hrs)"];
        const rows = tableData.map(u => [
            u.name,
            statusFilter,
            u.department || '-',
            u.stats.Technical,
            u.stats['Soft Skills'],
            u.stats.Knowledge,
            u.stats['Peer Learning'],
            u.stats.Total
        ]);
        
        let csvContent = "data:text/csv;charset=utf-8," 
            + headers.join(",") + "\n" 
            + rows.map(e => e.join(",")).join("\n");
            
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csvContent));
        link.setAttribute("download", `Training_Report_${selectedYear}_${statusFilter}.csv`);
        document.body.appendChild(link);
        link.click();
    };

    return (
        <div className="space-y-6">
            {/* CONTROLS */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col xl:flex-row gap-4 justify-between items-start xl:items-center">
                <div className="flex flex-wrap gap-3 w-full xl:w-auto">
                    {/* Search */}
                    <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 focus-within:bg-white focus-within:ring-2 focus-within:ring-[#20BEC6] w-full md:w-64">
                        <SearchIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <input className="bg-transparent outline-none text-sm w-full" placeholder="Search employee..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                    
                    {/* Status Filter (Active/Past) */}
                    <div className="flex items-center space-x-2">
                        <BriefcaseIcon className="h-4 w-4 text-gray-400" />
                        <select 
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-[#20BEC6]" 
                            value={statusFilter} 
                            onChange={e => setStatusFilter(e.target.value)}
                        >
                            <option value="Approved">Active Employees</option>
                            <option value="Deactivated">Past Employees</option>
                        </select>
                    </div>

                    {/* Dept Filter */}
                    <div className="flex items-center space-x-2">
                        <FilterIcon className="h-4 w-4 text-gray-400" />
                        <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-[#20BEC6]" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                            {uniqueDepts.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>

                    {/* Year Filter */}
                    <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-[#20BEC6] font-bold text-[#213368]" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
                        {yearOptions.map(y => <option key={y} value={y}>FY {y}</option>)}
                    </select>
                </div>

                <button onClick={handleExport} className="flex items-center space-x-2 text-sm font-bold text-[#213368] bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition whitespace-nowrap">
                    <DownloadIcon className="h-4 w-4" />
                    <span>Export CSV</span>
                </button>
            </div>

            {/* TABLE */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col min-h-[400px]">
                <div className="overflow-x-auto flex-1">
                    <table className="min-w-full text-left text-sm">
                        <thead className="bg-gray-50 text-gray-500 font-medium uppercase text-xs tracking-wider border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4">Employee</th>
                                <th className="px-6 py-4 text-right text-teal-700">Technical</th>
                                <th className="px-6 py-4 text-right text-orange-700">Soft Skills</th>
                                <th className="px-6 py-4 text-right text-blue-700">Knowledge</th>
                                <th className="px-6 py-4 text-right text-red-700">Peer</th>
                                <th className="px-6 py-4 text-right font-bold text-[#213368] bg-gray-50">Total Hrs</th>
                                <th className="px-6 py-4"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan="7" className="p-8 text-center text-gray-400">Loading data...</td></tr>
                            ) : currentItems.length === 0 ? (
                                <tr><td colSpan="7" className="p-8 text-center text-gray-400">No records found.</td></tr>
                            ) : (
                                currentItems.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50 transition cursor-pointer group" onClick={() => setSelectedMember(user)}>
                                        <td className="px-6 py-3">
                                            <div className="flex items-center">
                                                <UserAvatar url={user.photoURL} name={user.name} />
                                                <div className="ml-3">
                                                    <p className="font-bold text-[#213368]">{user.name}</p>
                                                    <p className="text-xs text-gray-400">{user.department}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3 text-right font-medium text-gray-600">{user.stats.Technical}</td>
                                        <td className="px-6 py-3 text-right font-medium text-gray-600">{user.stats['Soft Skills']}</td>
                                        <td className="px-6 py-3 text-right font-medium text-gray-600">{user.stats.Knowledge}</td>
                                        <td className="px-6 py-3 text-right font-medium text-gray-600">{user.stats['Peer Learning']}</td>
                                        <td className="px-6 py-3 text-right font-extrabold text-[#213368] bg-gray-50/50">{user.stats.Total}</td>
                                        <td className="px-6 py-3 text-right">
                                            <EyeIcon className="h-5 w-5 text-gray-300 group-hover:text-[#20BEC6] transition" />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION FOOTER */}
                {tableData.length > 0 && (
                    <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-between items-center">
                        <p className="text-xs text-gray-500">
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, tableData.length)} of {tableData.length} employees
                        </p>
                        <div className="flex space-x-2">
                            <button 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                                disabled={currentPage === 1} 
                                className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium text-gray-600"
                            >
                                Previous
                            </button>
                            <button 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                                disabled={currentPage === totalPages} 
                                className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium text-gray-600"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* DETAILS MODAL */}
            {selectedMember && (
                <HistoryModal 
                    user={selectedMember} 
                    year={selectedYear} 
                    onClose={() => setSelectedMember(null)} 
                />
            )}
        </div>
    );
}

function HistoryModal({ user, year, onClose }) {
    // Sort activities by date desc
    const activities = (user.activities || []).sort((a, b) => b.createdAt - a.createdAt);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
                <div className="p-5 border-b border-gray-200 bg-gray-50 flex justify-between items-center rounded-t-xl">
                    <div className="flex items-center space-x-3">
                        <UserAvatar url={user.photoURL} name={user.name} className="h-10 w-10" />
                        <div>
                            <h3 className="text-lg font-bold text-[#213368]">{user.name}</h3>
                            <p className="text-xs text-gray-500">Training Record - FY {year}</p>
                        </div>
                    </div>
                    <button onClick={onClose}><XIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" /></button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-0">
                    <table className="min-w-full text-left text-sm">
                        <thead className="bg-white text-gray-500 font-medium uppercase text-xs border-b border-gray-100 sticky top-0">
                            <tr>
                                <th className="px-6 py-3 bg-gray-50">Activity Title</th>
                                <th className="px-6 py-3 bg-gray-50">Type</th>
                                <th className="px-6 py-3 bg-gray-50">Date</th>
                                <th className="px-6 py-3 text-right bg-gray-50">Hours</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {activities.length === 0 ? (
                                <tr><td colSpan="4" className="p-6 text-center text-gray-400 italic">No completed trainings found for this period.</td></tr>
                            ) : (
                                activities.map(act => (
                                    <tr key={act.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-3">
                                            <div className="font-bold text-[#213368]">{act.title}</div>
                                            <div className="text-xs text-gray-500">{act.provider || 'Internal'}</div>
                                        </td>
                                        <td className="px-6 py-3">
                                            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">{act.type}</span>
                                        </td>
                                        <td className="px-6 py-3 text-gray-500">
                                            {act.date ? new Date(act.date.seconds * 1000).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-3 text-right font-bold text-[#213368]">{act.hours}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="p-4 border-t border-gray-200 bg-gray-50 text-right rounded-b-xl">
                    <span className="text-sm font-bold text-gray-600 mr-2">Total Hours:</span>
                    <span className="text-lg font-extrabold text-[#213368]">{user.stats.Total}</span>
                </div>
            </div>
        </div>
    );
}